import type { MouseEvent, ReactElement, ReactNode, RefObject } from "react";

import styles from "./Button.module.css";

export type ButtonVariant = "primary" | "secondary" | "ghost" | "danger";
export type ButtonSize = "sm" | "md";

export interface ButtonProps {
  readonly variant: ButtonVariant;
  readonly size?: ButtonSize | undefined;
  /** A square 32px button whose only child is an `Icon`; it then needs `label` (the mockup `.btn-icon`). */
  readonly iconOnly?: boolean | undefined;
  readonly type?: "button" | "submit" | undefined;
  readonly disabled?: boolean | undefined;
  readonly loading?: boolean | undefined;
  readonly onClick?: (() => void) | undefined;
  /**
   * The accessible name, for a button whose only content is an icon.
   *
   * Annex §11.3 does not list this prop, and adding it is a declared
   * deviation: F-UIX-007 requires every icon-only button to carry an
   * `aria-label` taken from the dictionary, and the alternative the annex
   * offers — a second, plainer button atom wrapping `Icon` — would give the
   * application two focus rings, two disabled behaviours and two places to fix
   * the next keyboard bug. One button, one contract.
   *
   * It stays OPTIONAL and unset by default: a button with visible text takes
   * its name from that text, and an `aria-label` on top of it would silently
   * replace what the reader can see.
   */
  readonly label?: string | undefined;
  /** A stable address, for the screen that must return the focus to this button. */
  readonly id?: string | undefined;
  /**
   * The id of what describes this button (`aria-describedby`), for a consequence the reader
   * should hear when reaching it, such as what a save will do. Unset by default.
   */
  readonly describedBy?: string | undefined;
  /** The keys that press this button from anywhere (`aria-keyshortcuts`), e.g. `Escape` (F-UIX-017). */
  readonly keyShortcuts?: string | undefined;
  /** It opens a layer (`aria-haspopup`): a dialog (popover, drawer) or a menu. */
  readonly hasPopup?: "dialog" | "menu" | undefined;
  /** Whether the layer it opens is open (`aria-expanded`). */
  readonly expanded?: boolean | undefined;
  /** A toggle's state (`aria-pressed`). */
  readonly pressed?: boolean | undefined;
  /** A handle on the element, for a layer anchored to it or a focus to give back. */
  readonly buttonRef?: RefObject<HTMLButtonElement | null> | undefined;
  readonly children: ReactNode;
}

/**
 * A real `<button type>`, never a `<div onClick>`.
 *
 * That single decision is what buys the whole keyboard contract of F-UIX-007
 * for free: Tab reaches it in visual order, Enter and Space activate it, and
 * `:focus-visible` paints the `--focus-ring` token. `loading` refuses every
 * press and announces itself with `aria-busy` and `aria-disabled`, so a slow
 * submit cannot be sent twice (checklist 13) and is not silent to a screen
 * reader. It is NOT the native `disabled`: a focused button that becomes
 * disabled drops the focus to the page (WCAG 2.4.3), so a loading button stays
 * focusable and its click is cancelled instead, which also cancels the submit
 * of its form, whether pressed or reached by Enter in a field.
 *
 * `label` exists for the one case the visible content cannot cover: a button
 * whose only child is an `Icon`, which is `aria-hidden` and therefore
 * nameless. ESLint refuses a literal there, so the name always comes from
 * `t()` (F-UIX-015).
 */
export function Button({
  variant,
  size = "md",
  iconOnly = false,
  type = "button",
  disabled = false,
  loading = false,
  onClick,
  label,
  id,
  describedBy,
  keyShortcuts,
  hasPopup,
  expanded,
  pressed,
  buttonRef,
  children,
}: ButtonProps): ReactElement {
  const className = buttonClassName(variant, size, iconOnly);
  const press = (event: MouseEvent<HTMLButtonElement>): void => {
    if (loading) {
      // Cancels the form submission a submit button would otherwise start.
      event.preventDefault();
      return;
    }
    onClick?.();
  };
  return (
    <button
      ref={buttonRef}
      type={type === "submit" ? "submit" : "button"}
      className={className}
      disabled={disabled}
      aria-disabled={loading && !disabled ? true : undefined}
      aria-busy={loading}
      id={id}
      aria-label={label}
      aria-describedby={describedBy}
      aria-keyshortcuts={keyShortcuts}
      aria-haspopup={hasPopup}
      aria-expanded={expanded}
      aria-pressed={pressed}
      onClick={press}
    >
      {children}
    </button>
  );
}

/** The classes of a `Button` of this look, for a link that must look like one (the mockup's `a.btn`). */
export function buttonClassName(variant: ButtonVariant, size: ButtonSize = "md", iconOnly = false): string {
  return [styles.button, styles[variant], styles[size], iconOnly ? styles.iconOnly : ""]
    .filter((name) => name !== "")
    .join(" ");
}
